import {
  RelationSchema,
  RelationType,
  ZodCreateRelationSchema,
  ZodGetRelationsParams,
  ZodUpdateRelationSchema,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Relation = RelationSchema;

export const getRelation = async (relationId: string): Promise<Relation> => {
  const response = await apiClient.get<{ relation: Relation }>(`/api/relation/${relationId}`);
  return response.data.relation;
};

/**
 * Get relations - returns all relations (for backward compatibility)
 * @deprecated Use getRelationsPaginated for better performance with large datasets
 */
export const getRelations = async (type?: RelationType): Promise<Relation[]> => {
  const params = type ? { type } : {};
  const response = await apiClient.get<{ relations: Relation[]; pagination: PaginationMeta }>("/api/relation", { params });
  return response.data.relations;
};

/**
 * Paginated response for relations
 */
export interface GetRelationsPaginatedResponse {
  relations: Relation[];
  pagination: PaginationMeta;
}

/**
 * Get relations with pagination support
 */
export const getRelationsPaginated = async (
  params?: ZodGetRelationsParams & PaginationParams,
): Promise<GetRelationsPaginatedResponse> => {
  const response = await apiClient.get<GetRelationsPaginatedResponse>("/api/relation", { params });
  return response.data;
};

export const createRelation = async (relationData: ZodCreateRelationSchema): Promise<Relation> => {
  const response = await apiClient.post<{ relation: Relation }>("/api/relation", relationData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.relation;
};

export const updateRelation = async (relationData: ZodUpdateRelationSchema): Promise<Relation> => {
  const response = await apiClient.put<{ relation: Relation }>(`/api/relation/${relationData.id}`, relationData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.relation;
};

export const deleteRelation = async (relationId: string): Promise<{ message: string; relation: Relation }> => {
  const response = await apiClient.delete<{ message: string; relation: Relation }>(`/api/relation/${relationId}`);
  return response.data;
};

/**
 * Fetch all relations by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllRelations = async (params?: ZodGetRelationsParams): Promise<Relation[]> => {
  const allRelations: Relation[] = [];
  let cursor: string | null = null;

  do {
    const response = await getRelationsPaginated({ ...params, cursor, limit: 100 });
    allRelations.push(...response.relations);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allRelations;
};

export default getRelation;
